import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Result,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ClockCircleOutlined,
  FileSearchOutlined,
  IdcardOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import { isPlatformOperatorUser } from '../../assets/utils/permissions';
import DashboardSection from '../dashboard/components/DashboardSection';
import {
  formatFieldValue,
  formatLogDate,
  humanizeActionName,
  humanizeFieldName,
  humanizeTableName,
  recordIdentityLabel,
  recordTypeLabel,
  targetRoute,
  targetSearchTerm,
  visibleFieldKeys,
} from './logDisplay';
import './logging-dashboard.css';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const ACTION_COLORS = {
  create: 'green',
  register: 'green',
  approve: 'green',
  approve_co: 'green',
  approve_hm: 'green',
  update: 'blue',
  propose: 'blue',
  query: 'gold',
  flag: 'gold',
  submit: 'gold',
  submit_preauth: 'gold',
  submit_claim: 'gold',
  reject: 'red',
  delete: 'red',
  unflag: 'default',
  login: 'default',
  logout: 'default',
  pay: 'purple',
  pay_claim: 'purple',
  payment_successful: 'purple',
  payment_verification_successful: 'purple',
  payment_failed: 'red',
  payment_verification_failed: 'red',
};

function actionColor(name) {
  const key = String(name || '').toLowerCase();
  return ACTION_COLORS[key] || 'default';
}

function formatCellValue(field, value) {
  const text = formatFieldValue(field, value);
  if (text === '—') return <Text type="secondary">—</Text>;
  if (text.includes('\n')) {
    return <Text className="logs-field-value logs-field-value--multiline">{text}</Text>;
  }
  return <Text className="logs-field-value">{text}</Text>;
}

/** Field-by-field before/after comparison — easier to scan than raw JSON for ops staff. */
const FieldDiffTable = ({ before, after }) => {
  const beforeObj = before && typeof before === 'object' ? before : {};
  const afterObj = after && typeof after === 'object' ? after : {};
  const keys = useMemo(
    () => visibleFieldKeys(beforeObj, afterObj),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [before, after]
  );

  if (!keys.length) {
    return <Empty description="No details were recorded for this change" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Table
      size="small"
      pagination={false}
      rowKey="field"
      dataSource={keys.map((k) => ({ field: k, before: beforeObj[k], after: afterObj[k] }))}
      columns={[
        {
          title: 'Field',
          dataIndex: 'field',
          width: 200,
          render: (v) => <Text className="logs-field-name">{humanizeFieldName(v)}</Text>,
        },
        {
          title: 'Before',
          dataIndex: 'before',
          render: (v, row) => formatCellValue(row.field, v),
        },
        {
          title: 'After',
          dataIndex: 'after',
          render: (v, row) => {
            const changed = formatFieldValue(row.field, row.before) !== formatFieldValue(row.field, v);
            const cell = formatCellValue(row.field, v);
            return changed ? <span className="logs-field-changed">{cell}</span> : cell;
          },
        },
      ]}
    />
  );
};

/**
 * Org and policy deactivations used to share one "deactivated" filter, which
 * was useless for answering "was this *org* switched off, or just this policy".
 * The cron writes them as distinct messages against distinct tables
 * (see cronRoll in api_v1/public/legacy.php), so they split cleanly.
 *
 * `subject_auto: '0'` stops the API from treating the phrase as an identifier
 * to expand — these are deliberate text searches.
 */
const QUICK_FILTERS = [
  {
    key: 'org_deactivations',
    label: 'Org deactivation',
    q: 'deactivated organization',
    target_table: 'organizations',
    subject_auto: '0',
  },
  {
    key: 'policy_deactivations',
    label: 'Policy deactivation',
    q: 'deactivated policy',
    target_table: 'clients',
    subject_auto: '0',
  },
  { key: 'renewals', label: 'Org expiry / renewals', q: 'Expiry Updated', subject_auto: '0' },
  { key: 'organizations', label: 'Organizations', target_table: 'organizations' },
  { key: 'clients', label: 'Client policies', target_table: 'clients' },
  { key: 'pa', label: 'PA / encounters', target_table: 'checkin' },
  { key: 'claims', label: 'Claims', target_table: 'claims' },
];

const DEFAULT_FILTERS = {
  q: '',
  target_table: undefined,
  target_id: '',
  action_id: undefined,
  application_id: undefined,
  type_id: undefined,
  actor_q: '',
  user_id: undefined,
  facility_id: '',
  subject_auto: undefined,
  date_from: undefined,
  date_to: undefined,
};

function actorLabel(row) {
  if (row?.actor_name) return row.actor_name;
  if (row?.actor_email) return row.actor_email;
  if (row?.logs_user_id) return `User #${row.logs_user_id}`;
  return 'System';
}

/** Second line under the actor: the identifiers that pin down *which* staff member. */
function actorIdentifiers(row) {
  const parts = [];
  if (row?.actor_staff_id) parts.push(row.actor_staff_id);
  if (row?.actor_email) parts.push(row.actor_email);
  if (!parts.length && row?.logs_user_id) parts.push(`User #${row.logs_user_id}`);
  return parts.join(' · ');
}

function staffOptionLabel(s) {
  return [s.name, s.staff_id, s.email].filter(Boolean).join(' · ') || `Staff #${s.user_id}`;
}

const SUBJECT_LABELS = {
  enrollee: 'Enrollee',
  policy: 'Policy',
  encounter: 'Encounter',
  facility: 'Facility',
  organization: 'Organization',
};

const Logs = ({ compact = false }) => {
  const { user } = useAuth();
  const { canAccess } = usePermissions();
  const allowed = useMemo(
    () => canAccess('activityLog') || isPlatformOperatorUser(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [facets, setFacets] = useState({
    target_tables: [],
    target_table_options: [],
    actions: [],
    applications: [],
    types: [],
    staff: [],
  });
  const [subject, setSubject] = useState(null);

  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const navigate = useNavigate();

  const loadFacets = useCallback(async () => {
    const parsed = lib.parseApiEnvelope(await lib.getAdminLogsFacets());
    if (parsed.ok) {
      const tables = parsed.data?.target_tables || [];
      setFacets({
        target_tables: tables,
        // Older API builds only send the flat list — derive labels locally.
        target_table_options: parsed.data?.target_table_options
          || tables.map((t) => ({ value: t, label: humanizeTableName(t) })),
        actions: parsed.data?.actions || [],
        applications: parsed.data?.applications || [],
        types: parsed.data?.types || [],
        staff: parsed.data?.staff || [],
      });
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, per_page: perPage };
      Object.keys(params).forEach((k) => {
        if (params[k] === undefined || params[k] === '') delete params[k];
      });
      const parsed = lib.parseApiEnvelope(await lib.getAdminLogs(params));
      if (!parsed.ok) {
        const fallback = parsed.httpStatus === 405
          ? 'Activity log endpoint is not available (method not allowed). Try again after the API is updated.'
          : 'Failed to load activity log';
        setError(parsed.message || fallback);
        lib.notifyApiEnvelope(parsed, { errorFallback: fallback });
        setRows([]);
        setTotal(0);
        setSubject(null);
        return;
      }
      setError(null);
      setRows(parsed.data?.rows || []);
      setTotal(parsed.data?.total || 0);
      setSubject(parsed.data?.subject || null);
    } finally {
      setLoading(false);
    }
  }, [filters, page, perPage]);

  useEffect(() => {
    if (allowed) loadFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  useEffect(() => {
    if (allowed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, load]);

  const applyQuickFilter = (qf) => {
    setPage(1);
    setDateRange(null);
    setFilters({
      ...DEFAULT_FILTERS,
      ...(qf.q ? { q: qf.q } : {}),
      ...(qf.target_table ? { target_table: qf.target_table } : {}),
      ...(qf.subject_auto ? { subject_auto: qf.subject_auto } : {}),
    });
  };

  /** Jump the log to everything recorded about one record. */
  const searchForTarget = (term) => {
    if (!term) return;
    setPage(1);
    setDateRange(null);
    setFilters({ ...DEFAULT_FILTERS, q: String(term) });
  };

  const resetFilters = () => {
    setPage(1);
    setDateRange(null);
    setFilters(DEFAULT_FILTERS);
  };

  const openDetail = async (row) => {
    setSelected(row);
    setDetailLoading(true);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getAdminLogEntry(row.logs_id));
      if (parsed.ok) {
        setSelected(parsed.data);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Admins only"
        subTitle="The activity log is available to super admins and platform operators (admin.* / platform.*)."
      />
    );
  }

  const columns = [
    {
      title: 'Date',
      dataIndex: 'logs_date',
      width: 168,
      render: (v) => (
        <Space size={4}>
          <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
          <Text style={{ fontSize: 12 }}>{formatLogDate(v)}</Text>
        </Space>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'app_action_name',
      width: 120,
      render: (v) => <Tag color={actionColor(v)}>{humanizeActionName(v)}</Tag>,
    },
    {
      // "Administration_Staff" told ops nothing about *who* acted. Name, staff
      // code and email are resolved server-side (AdminLogsService::attachActors).
      title: 'Performed by',
      key: 'actor',
      width: 230,
      render: (_, row) => {
        const identifiers = actorIdentifiers(row);
        return (
          <div className="logs-actor">
            <Text style={{ fontSize: 13 }} strong={!!row.actor_name}>{actorLabel(row)}</Text>
            {identifiers ? (
              <Text type="secondary" className="logs-actor__ids" style={{ fontSize: 12 }}>
                {identifiers}
              </Text>
            ) : null}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.log_type_name || (row.logs_user_id ? 'Staff' : 'System')}
            </Text>
          </div>
        );
      },
    },
    {
      // The old column showed the raw table name and a bare id ("costable_pricing
      // · 4213"). The API now resolves the record itself, so this shows what was
      // actually affected and — where a profile page exists — links to it.
      title: 'Target',
      key: 'target',
      width: 280,
      render: (_, row) => {
        const route = targetRoute(row.target_link);
        const name = row.target_display || row.logs_target_id || '—';
        const heading = route ? (
          <Button
            type="link"
            size="small"
            className="logs-target__link"
            icon={<LinkOutlined />}
            onClick={(e) => { e.stopPropagation(); navigate(route); }}
          >
            {name}
          </Button>
        ) : (
          <Text style={{ fontSize: 13 }}>{name}</Text>
        );

        return (
          <div className="logs-target">
            {heading}
            {row.target_subtitle ? (
              <Text type="secondary" style={{ fontSize: 12 }}>{row.target_subtitle}</Text>
            ) : null}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {recordTypeLabel(row)}
              {row.logs_target_id && row.target_display ? ` · ${row.logs_target_id}` : ''}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Message',
      dataIndex: 'logs_message',
      ellipsis: true,
      render: (v) => <Text>{v || <Text type="secondary">—</Text>}</Text>,
    },
    {
      title: 'App',
      key: 'application',
      width: 120,
      ellipsis: true,
      render: (_, row) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {row.application_display_name || row.application_name || '—'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'logs_action_status',
      width: 80,
      render: (v) => (Number(v) === 1 ? <Tag color="success">Succeeded</Tag> : <Tag color="error">Failed</Tag>),
    },
    {
      title: '',
      key: 'view',
      width: 150,
      render: (_, row) => {
        const term = targetSearchTerm(row);
        return (
          <Space size={4}>
            <Button size="small" icon={<FileSearchOutlined />} onClick={(e) => { e.stopPropagation(); openDetail(row); }}>
              View
            </Button>
            {term ? (
              <Tooltip title="Show everything recorded for this record">
                <Button
                  size="small"
                  type="text"
                  icon={<SearchOutlined />}
                  onClick={(e) => { e.stopPropagation(); searchForTarget(term); }}
                />
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <div className={`logs-dashboard${compact ? ' logs-dashboard--compact' : ''}`}>
      <div className="dashboard-page-stack">
        {!compact ? (
          <header className="dash-masthead">
            <div className="dash-masthead__lead">
              <h1 className="dash-masthead__title">Activity log</h1>
              <p className="dash-masthead__meta">
                Search by Enrollee ID, Policy number, Encounter code, Facility ID, org name or
                code, or any text in a log message. Enrollee and policy searches include PA and
                claims activity, not just profile edits.
              </p>
            </div>
          </header>
        ) : null}

        {error ? (
          <Alert
            type="error"
            showIcon
            closable
            message="Could not load activity log"
            description={error}
            onClose={() => setError(null)}
          />
        ) : null}

        <DashboardSection id="activity-log-filters" icon={<SearchOutlined />} title="Search" collapsible={false} variant="content">
          <Input.Search
            allowClear
            size="large"
            placeholder="Enrollee ID, policy no, encounter code, facility ID, org name/code, message text…"
            prefix={<SearchOutlined />}
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, subject_auto: undefined }))}
            onSearch={() => { setPage(1); load(); }}
          />

          {subject ? (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message={`Showing all activity for ${SUBJECT_LABELS[subject.type] || subject.type} ${subject.label || subject.id}`}
              description={
                subject.truncated
                  ? 'This record has a very large history — only the most recent related records were matched. Narrow the date range for a complete view.'
                  : 'Includes PA decisions, claims, encounters and profile changes linked to this record.'
              }
            />
          ) : null}

          <div className="logs-quick-filters">
            <Text type="secondary" style={{ fontSize: 12 }}>Quick filters:</Text>
            {QUICK_FILTERS.map((qf) => (
              <Button key={qf.key} size="small" onClick={() => applyQuickFilter(qf)}>
                {qf.label}
              </Button>
            ))}
            <Button size="small" onClick={resetFilters}>Clear all</Button>
          </div>

          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Record type</Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Any record type"
                value={filters.target_table}
                onChange={(v) => setFilters((f) => ({ ...f, target_table: v }))}
                options={facets.target_table_options}
                showSearch
                optionFilterProp="label"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Record id (exact)</Text>
              <Input
                allowClear
                placeholder="Exact id / policy_no / insurance_no"
                value={filters.target_id}
                onChange={(e) => setFilters((f) => ({ ...f, target_id: e.target.value }))}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Action</Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Any action"
                value={filters.action_id}
                onChange={(v) => setFilters((f) => ({ ...f, action_id: v }))}
                options={facets.actions.map((a) => ({ value: a.app_action_id, label: a.app_action_name }))}
                showSearch
                optionFilterProp="label"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Application</Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Any application"
                value={filters.application_id}
                onChange={(v) => setFilters((f) => ({ ...f, application_id: v }))}
                options={facets.applications.map((a) => ({
                  value: a.app_module_id,
                  label: a.app_module_name || a.app_module_codeed_name,
                }))}
                showSearch
                optionFilterProp="label"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Actor type</Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Any actor type"
                value={filters.type_id}
                onChange={(v) => setFilters((f) => ({ ...f, type_id: v }))}
                options={facets.types.map((t) => ({ value: t.log_type_id, label: t.log_type_name }))}
                showSearch
                optionFilterProp="label"
              />
            </Col>
            {/* Review everything one admin did: pick them from the list, or type
                a staff code / email fragment if they are not in it. */}
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Admin staff</Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Any admin staff"
                value={filters.user_id}
                onChange={(v) => setFilters((f) => ({ ...f, user_id: v }))}
                options={facets.staff.map((st) => ({
                  value: st.user_id,
                  label: staffOptionLabel(st),
                }))}
                showSearch
                optionFilterProp="label"
                suffixIcon={<TeamOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Staff email / staff ID</Text>
              <Input
                allowClear
                prefix={<IdcardOutlined />}
                placeholder="e.g. AM-0142 or jane@amanhmo.com"
                value={filters.actor_q}
                onChange={(e) => setFilters((f) => ({ ...f, actor_q: e.target.value }))}
                onPressEnter={() => { setPage(1); load(); }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Text type="secondary" style={{ fontSize: 12 }}>Facility ID</Text>
              <Input
                allowClear
                placeholder="e.g. 214"
                value={filters.facility_id}
                onChange={(e) => setFilters((f) => ({ ...f, facility_id: e.target.value.replace(/\D/g, '') }))}
                onPressEnter={() => { setPage(1); load(); }}
              />
            </Col>
            <Col xs={24} sm={12} md={10}>
              <Text type="secondary" style={{ fontSize: 12 }}>Date range</Text>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                showTime
                onChange={(range) => {
                  setDateRange(range);
                  setFilters((f) => ({
                    ...f,
                    date_from: range?.[0] ? range[0].format('YYYY-MM-DD HH:mm:ss') : undefined,
                    date_to: range?.[1] ? range[1].format('YYYY-MM-DD HH:mm:ss') : undefined,
                  }));
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <Button type="primary" icon={<SearchOutlined />} onClick={() => { setPage(1); load(); }} loading={loading}>
                Apply filters
              </Button>
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                Refresh
              </Button>
            </Col>
          </Row>
        </DashboardSection>

        <DashboardSection
          id="activity-log-results"
          icon={<FileSearchOutlined />}
          title="Results"
          subtitle={loading ? 'Loading…' : `${total.toLocaleString()} entries`}
          collapsible={false}
          variant="content"
        >
          <div className="logs-table-wrap">
            <Table
              rowKey="logs_id"
              loading={loading}
              columns={columns}
              dataSource={rows}
              onRow={(row) => ({ onClick: () => openDetail(row), style: { cursor: 'pointer' } })}
              pagination={{
                current: page,
                pageSize: perPage,
                total,
                showSizeChanger: true,
                pageSizeOptions: [25, 50, 100, 200],
                onChange: (p, ps) => { setPage(p); setPerPage(ps); },
              }}
              scroll={{ x: 1100 }}
            />
          </div>
        </DashboardSection>
      </div>

      <Drawer
        title={selected ? `${humanizeActionName(selected.app_action_name)} · ${recordTypeLabel(selected)}` : ''}
        open={!!selected}
        onClose={() => setSelected(null)}
        width={720}
      >
        {selected && (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="When">{formatLogDate(selected.logs_date)}</Descriptions.Item>
              <Descriptions.Item label="What happened">
                <Tag color={actionColor(selected.app_action_name)}>{humanizeActionName(selected.app_action_name)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Who did it">
                <Space direction="vertical" size={0}>
                  <Text strong>{actorLabel(selected)}</Text>
                  {selected.actor_staff_id ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>Staff ID: {selected.actor_staff_id}</Text>
                  ) : null}
                  {selected.actor_email ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>{selected.actor_email}</Text>
                  ) : null}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {selected.log_type_name || 'System'}
                    {selected.logs_user_id ? ` · user #${selected.logs_user_id}` : ''}
                  </Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Which app">
                {selected.application_display_name || selected.application_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Record type">{recordTypeLabel(selected)}</Descriptions.Item>
              <Descriptions.Item label="Record">
                <Space direction="vertical" size={0}>
                  {targetRoute(selected.target_link) ? (
                    <Button
                      type="link"
                      size="small"
                      className="logs-target__link"
                      icon={<LinkOutlined />}
                      onClick={() => navigate(targetRoute(selected.target_link))}
                    >
                      {recordIdentityLabel(selected)}
                    </Button>
                  ) : (
                    <Text>{recordIdentityLabel(selected)}</Text>
                  )}
                  {selected.target_subtitle ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>{selected.target_subtitle}</Text>
                  ) : null}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Summary">{selected.logs_message || '—'}</Descriptions.Item>
              {selected.logs_misc ? (
                <Descriptions.Item label="Notes">{selected.logs_misc}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Result">
                {Number(selected.logs_action_status) === 1 ? (
                  <Tag color="success">Succeeded</Tag>
                ) : (
                  <Tag color="error">Failed</Tag>
                )}
              </Descriptions.Item>
              {selected.logs_ip_address ? (
                <Descriptions.Item label="IP address">{selected.logs_ip_address}</Descriptions.Item>
              ) : null}
              {selected.logs_user_agent ? (
                <Descriptions.Item label="Device / browser">
                  <Tooltip title={selected.logs_user_agent}>
                    <Text ellipsis style={{ maxWidth: 480, display: 'inline-block' }}>{selected.logs_user_agent}</Text>
                  </Tooltip>
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            <div>
              <Title level={5}>What changed</Title>
              <FieldDiffTable
                before={selected.logs_target_old_data_decoded}
                after={selected.logs_target_new_data_decoded}
              />
            </div>

            {(selected.logs_misc_old_data_decoded || selected.logs_misc_new_data_decoded) && (
              <div>
                <Title level={5}>Extra details</Title>
                <FieldDiffTable
                  before={selected.logs_misc_old_data_decoded}
                  after={selected.logs_misc_new_data_decoded}
                />
              </div>
            )}

            <div>
              <Title level={5}>How it looks now</Title>
              {detailLoading && <Text type="secondary">Loading…</Text>}
              {!detailLoading && selected.current_target && (
                <FieldDiffTable before={selected.logs_target_new_data_decoded} after={selected.current_target} />
              )}
              {!detailLoading && !selected.current_target && (
                <Alert
                  type="info"
                  showIcon
                  message="Current details are not available"
                  description="This record may have been removed, or it can no longer be matched from the original log."
                />
              )}
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export { Logs };
export default Logs;
